'use client';

// imports
import Link from 'next/link';
import { Scan, Brain, BarChart2, MessageCircle, ArrowRight, Info, AlertTriangle, CircleQuestionMark } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTASection from '../home-page/components/CTASection';
import { useInView } from '@/hooks/useInView';

// constants
const base = 'transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]';
const hidden = 'opacity-0 translate-y-8';
const show = 'opacity-100 translate-y-0';

// data
const steps = [
  {
    number: '01',
    icon: Scan,
    title: 'Start Scan',
    description:
      'Upload a clear photo of your tongue using your device camera or file picker. Our system accepts standard image formats and guides you through best practices for a quality capture.',
    color: 'from-blue-500 to-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-100',
    iconColor: 'text-blue-600',
    numberColor: 'text-blue-200',
  },
  {
    number: '02',
    icon: Brain,
    title: 'AI Analysis',
    description:
      'Our deep learning model processes the image, identifying visual patterns, color variations, coating textures, and surface anomalies associated with various diabetes health indicators.',
    color: 'from-indigo-500 to-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-100',
    iconColor: 'text-indigo-600',
    numberColor: 'text-indigo-200',
  },
  {
    number: '03',
    icon: BarChart2,
    title: 'View Results',
    description:
      'Within moments, you’ll receive a structured and easy-to-understand results. This includes detected indicators, confidence levels, and a clear summary designed to help you interpret the findings.',
    color: 'from-sky-500 to-cyan-500',
    bg: 'bg-sky-50',
    border: 'border-sky-100',
    iconColor: 'text-sky-600',
    numberColor: 'text-sky-200',
  },
  {
    number: '04',
    icon: MessageCircle,
    title: 'Ask AI Assistant',
    description:
      'Use the built-in AI chat to ask follow-up questions about your results, understand specific findings, or learn more about diabetes and tongue health—all in a clear and easy-to-understand way.',
    color: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-50',
    border: 'border-violet-100',
    iconColor: 'text-violet-600',
    numberColor: 'text-violet-200',
  },
];

// component
export default function HowItWorksPage() {

  // hooks
  const { ref: heroRef, isVisible: heroVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  const { ref: stepsRef, isVisible: stepsVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  const { ref: disclaimerRef, isVisible: disclaimerVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* hero section */}
      <section ref={heroRef} className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-sky-50 to-indigo-50 pt-20 pb-20">
        {/* dot grid */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'radial-gradient(circle, #93c5fd 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        {/* glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-blue-200/30 rounded-full blur-3xl pointer-events-none" />

        <div className={`${base} ${heroVisible ? show : hidden} relative max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16 text-center`}>
          {/* badge */}
          <div className="inline-flex items-center gap-2 bg-white/80 border border-blue-100 rounded-full px-4 py-1.5 text-sm font-medium text-blue-600 shadow-sm mb-6">
            <Brain size={14} />
            AI-Powered Tongue Analysis
          </div>

          <h1 className={`${base} delay-100 ${heroVisible ? show : hidden} text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-5 leading-tight`}>
            How{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Insighta AI
            </span>{' '}
            Works
          </h1>
          <p className= {`${base} delay-200 ${heroVisible ? show : hidden} text-lg md:text-xl text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed`}>
            From a single photo to actionable diabetes health insights — our four-step process makes
            AI-powered tongue analysis simple, fast, and accessible to everyone.
          </p>
          <Link
            href="/upload-page"
            className={`${base} delay-300 ${heroVisible ? show : hidden} inline-flex items-center gap-2 
              bg-gradient-to-r from-blue-500 to-blue-600 
              text-white font-semibold rounded-full 
              px-7 py-3.5 text-base 
              shadow-lg shadow-blue-500/25
              hover:shadow-blue-500/40
              hover:from-blue-600 hover:to-blue-700
              hover:scale-[1.02]
              transition-all duration-200 active:scale-95
              group`}
          >
            <Scan size={18} />

            Try It Now

            <ArrowRight
              size={16}
              className="group-hover:translate-x-1 transition-transform duration-200"
            />
          </Link>
        </div>
      </section>

      {/* steps section */}
      <section ref={stepsRef} className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16">
          <div className="text-center mb-14">
              {/* BADGE */}
              <div className={`${base} ${stepsVisible ? show : hidden} inline-flex items-center gap-2 px-3 py-1.5 rounded-full 
                bg-blue-500/10 text-blue-600 text-xs font-bold tracking-widest uppercase 
                border border-blue-400/20 mb-6`}>

                <CircleQuestionMark className="w-3.5 h-3.5 text-blue-500" />
                How it Works
              </div>
            <h2 className= {`${base} delay-100 ${stepsVisible ? show : hidden} text-3xl md:text-4xl font-bold text-slate-900 mb-3`}>
              Four Simple Steps
            </h2>
            <p className={`${base} delay-200 ${stepsVisible ? show : hidden} text-slate-500 text-lg max-w-xl mx-auto`}>
              Everything happens in minutes — no appointments, no equipment, no expertise required.
            </p>
          </div>

          {/* connector line */}
          <div className="hidden lg:block relative mb-0">
            <div className="absolute top-[52px] left-[12.5%] right-[12.5%] h-[2px] bg-gray-200 z-0 overflow-hidden">

            <div
              className={`
                absolute top-0 left-0 h-full w-[180px]
                bg-[linear-gradient(to_right,transparent,theme(colors.blue.500),theme(colors.purple.500),theme(colors.pink.500),theme(colors.orange.400),transparent)]
                opacity-90 blur-[1.5px]
                ${stepsVisible ? 'opacity-90' : 'opacity-0'}
              `}
              style={{
                animation: stepsVisible
                  ? 'beam-move 3s linear infinite 300ms'
                  : 'none',
              }}
            />

            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {steps?.map((step, index) => {
              const Icon = step?.icon;
              return (
                <div
                  key={step?.number}
                  className={`
                    h-full
                    ${stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                    transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]
                  `}
                  style={{
                    transitionDelay: `${300 + index * 120}ms`,
                  }}
                >
                  <div
                    className={`
                      relative rounded-2xl border ${step?.border} ${step?.bg}
                      p-6 flex flex-col gap-4 h-full

                      transition-all duration-300
                      hover:-translate-y-1 hover:shadow-md hover:scale-[1.02]
                    `}
                  >
                    {/* number */}
                    <span className={`absolute top-4 right-5 text-5xl font-black ${step?.numberColor} select-none leading-none`}>
                      {step?.number}
                    </span>
                    {/* icon circle */}
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step?.color} flex items-center justify-center shadow-sm`}>
                      <Icon size={22} className="text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">{step?.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{step?.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* behind insighta section*/}
      <section
       ref={disclaimerRef}
        id="behind-insighta"
        className="py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden"
      >
        {/* bg*/}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-10 xl:px-16">

          {/* header*/}
          <div className="text-center mb-14">

            {/* badge */}
            <div className={`${base} ${disclaimerVisible ? show : hidden} inline-flex items-center gap-2 
              bg-white/10 border border-white/20 text-blue-300 
              rounded-full px-4 py-1.5 text-xs font-semibold mb-4 uppercase tracking-wider`}>
              
              <Brain size={14} />
              Technology
            </div>

            {/* title */}
            <h2 className={`${base} delay-100 ${disclaimerVisible ? show : hidden} text-3xl md:text-4xl font-bold text-white mb-4 leading-tight`}>
              Behind{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Insighta
              </span>
              <span className="text-gray-500"> AI</span>
            </h2>

            {/* description */}
            <p className={`${base} delay-200 ${disclaimerVisible ? show : hidden} text-slate-400 text-base max-w-2xl mx-auto leading-relaxed`}>
              Understanding the technology and its boundaries helps you use it responsibly.
            </p>

          </div>

          {/*cards*/}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto items-stretch">

            {/* how ai works*/}
            <div
              className={`
                h-full
                ${disclaimerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]
              `}
              style={{ transitionDelay: '300ms' }}
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 h-full">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-sm">
                    <Info size={20} className="text-white" />
                  </div>

                  <div>
                    {/* badge */}
                    <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full 
                      bg-blue-500/15 text-blue-300 border border-blue-400/30">
                      AI MODEL
                    </span>

                    <h3 className="text-white font-semibold mt-1">
                      How the AI Model Works
                    </h3>
                  </div>
                </div>


                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Insighta is a deep learning–based screening support system that analyzes tongue images to 
                  identify visual patterns potentially associated with diabetes-related conditions.
                  The system first isolates the tongue region through image preprocessing and segmentation. 
                  The processed image is then analyzed by trained neural network models to learn visual 
                  characteristics such as color variation, coating patterns, and surface texture.
                </p>

                <p className="text-slate-400 text-sm leading-relaxed">
                  These learned patterns are used to generate a probabilistic prediction, indicating the 
                  likelihood of diabetes-related indicators, along with a confidence score reflecting model certainty.
                  To improve interpretability, the system combines model outputs with a curated knowledge base to produce structured, easy-to-understand insights. 
                  These explanations are generated through an AI-assisted layer that translates technical results into user-friendly summaries.
                </p>
              </div>
            </div>

            {/* disclaimer */}
            <div
              className={`
                h-full
                ${disclaimerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]
              `}
              style={{ transitionDelay: '450ms' }}
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 h-full">

                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center shadow-sm">
                    <AlertTriangle size={20} className="text-white" />
                  </div>

                  <div>
                    {/* badge */}
                    <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full 
                      bg-amber-500/15 text-amber-300 border border-amber-400/30">
                      DISCLAIMER
                    </span>

                    <h3 className="text-white font-semibold mt-1">
                      Not a Medical Diagnosis
                    </h3>
                  </div>
                </div>

                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                  Insighta is a research-based, non-invasive screening support tool and is not 
                  intended to provide medical diagnosis, treatment, or clinical decision-making.
                  The results are generated from machine learning models and should be interpreted as preliminary insights only, not as definitive health assessments. 
                  The system does not replace laboratory testing or professional medical evaluation.
                </p>

                <p className="text-slate-400 text-sm leading-relaxed mb-5">
                  Users are strongly encouraged to consult qualified healthcare professionals for proper diagnosis and appropriate medical guidance.
                </p>

                {/* bottom badge */}
                <div className="inline-flex items-center gap-2 
                  bg-amber-500/10 border border-amber-400/20 
                  rounded-full px-4 py-1.5 text-xs font-semibold text-amber-300">
                  
                  <AlertTriangle size={12} />
                  For research and informational purposes only
                </div>

              </div>

            </div>
          </div>

        </div>
      </section>
      
      <CTASection sectionBg="bg-white" />
      
      <Footer />
    </div>
  );
}
